import { AND, EQ, HAS, NOT, TYPE, UID } from 'shared/api/query/constants';
import { CACHED_UID_SEPARATOR, CACHED_VALUE_SEPARATOR, UNASSIGNED_VALUE } from '@/composables/grid-page/chart/utils';
import { aggregatorType, chartGroupByOption, propertyType } from 'shared/constants.json';
import { combine } from 'shared/api/query/filter';

// Dgraph's @groupby parser rejects `val()` as an aggregator argument and its
// executor refuses sum/avg/min/max on string @lang predicates — so cached
// number properties can't be aggregated server-side. This module owns the
// client-side pipeline that compensates: raw-row fetch, node-ref uid hex
// normalization, synthetic `___UID___0` sentinel routing, and folding the
// unassigned bucket into groupByData so chart-grouping weights it correctly.
//
// `decodeChartData` returns the same `{ groupByData, unassignedValue,
// noStackData }` shape as the @groupby path; callers dispatch on
// `isPropertyNumericAgg` without seeing any of these quirks.
export default function usePropertyNumericChartPath({
  chart,
  hasStacking,
  isAlwaysSet,
  isNumericAggregator,
  isGoalCycleGroupingWithFilter,
  buildNotHasFilter,
  getEdge,
  getLangs,
}) {
  // Edges that resolve to linked nodes need a `{ uid }` sub-child: Dgraph
  // returns the node, not the hex UID @groupby auto-emits. Scalar edges
  // (date/number/text) need no sub-child.
  const NODE_REF_EDGE_TYPES = [propertyType.options, propertyType.user, propertyType.space, propertyType.status];
  const isNodeRefEdge = (prop) => !prop.isProperty && NODE_REF_EDGE_TYPES.includes(prop.edgeType);

  // Sub-field `uid` is a decimal JSON number; the resolver (`findByHexUID`)
  // does `parseInt(value, 16)` and expects the `"0x..."` form @groupby emits.
  const toHexUid = (uid) => (typeof uid === 'number' ? `0x${uid.toString(16)}` : uid);

  const normalizeAxisValue = (raw, prop) => {
    if (raw === undefined || raw === null) return raw;
    if (!isNodeRefEdge(prop)) return raw;
    const nodes = Array.isArray(raw) ? raw : [raw];
    const uids = nodes.map((n) => (n?.uid != null ? toHexUid(n.uid) : null)).filter(Boolean);
    if (uids.length === 0) return undefined;
    if (uids.length === 1) return uids[0];
    return uids.join(CACHED_VALUE_SEPARATOR);
  };

  // `<displayValue>___UID___0` is a "no real value" sentinel — most notably
  // PropertyOptionStatusNone for goals with ProgressMeasurement = None (see
  // pkg/xfmr/service/goal/progress/status.go). No real entity has uid 0.
  const isSyntheticNoUidValue = (raw) => {
    if (typeof raw !== 'string') return false;
    const parts = raw.split(CACHED_UID_SEPARATOR);
    return parts.length === 2 && parts[1] === '0';
  };

  const partitionRows = (rows) => {
    if (!chart.value.groupBy.isProperty) return { realRows: rows, syntheticRows: [] };
    const realRows = [];
    const syntheticRows = [];
    rows.forEach((row) => {
      if (isSyntheticNoUidValue(row.val)) syntheticRows.push(row);
      else realRows.push(row);
    });
    return { realRows, syntheticRows };
  };

  // `has` alone keeps "" values; `not eq("")` alone keeps unset langs because
  // Dgraph treats absence as not-equal — so we need both.
  const aggHasValueFilter = () => {
    const lang = `p${chart.value.aggregationConfig.aggregation.property.uid}`;
    return combine(AND, [
      { func: { name: HAS, attr: 'cachedProperty', lang } },
      { op: NOT, child: [{ func: { name: EQ, attr: 'cachedProperty', lang, args: [{ value: '' }] } }] },
    ]);
  };

  const buildAxisChild = (alias, prop) => {
    const base = { alias, attr: getEdge(prop), langs: getLangs(prop) };
    if (isNodeRefEdge(prop)) return { ...base, children: [{ attr: UID }] };
    return base;
  };

  const buildRowChildren = () => {
    const groupBy = chart.value.groupBy;
    const stack = chart.value.aggregationConfig.stackOptions;
    const agg = chart.value.aggregationConfig.aggregation;
    const children = [
      buildAxisChild('val', groupBy),
      { alias: 'aggValue', attr: 'cachedProperty', langs: getLangs(agg) },
    ];
    if (hasStacking.value) children.push(buildAxisChild('stackGroup', stack));
    return children;
  };

  const buildBlock = (alias, scopeFilter) => ({
    alias,
    func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
    filter: combine(AND, [scopeFilter, aggHasValueFilter()]),
    model: chart.value.source,
    children: buildRowChildren(),
  });

  const transformRows = (rows) => {
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const stacked = hasStacking.value;
    const groupBy = chart.value.groupBy;
    const stackOptions = chart.value.aggregationConfig.stackOptions;
    return rows.reduce((acc, row) => {
      const parsed = parseFloat(row.aggValue);
      if (Number.isNaN(parsed)) return acc;
      const item = { val: normalizeAxisValue(row.val, groupBy), count: 1, [aggregator]: parsed };
      if (stacked) item.stackGroup = normalizeAxisValue(row.stackGroup, stackOptions);
      acc.push(item);
      return acc;
    }, []);
  };

  const aggregateValues = (rows, aggregator) => {
    const values = rows.reduce((acc, row) => {
      const n = parseFloat(row.aggValue);
      if (!Number.isNaN(n)) acc.push(n);
      return acc;
    }, []);
    if (values.length === 0) return 0;
    switch (aggregator) {
      case aggregatorType.sum: return values.reduce((a, b) => a + b, 0);
      case aggregatorType.avg: return values.reduce((a, b) => a + b, 0) / values.length;
      case aggregatorType.min: return values.reduce((a, b) => (b < a ? b : a));
      case aggregatorType.max: return values.reduce((a, b) => (b > a ? b : a));
      default: return 0;
    }
  };

  // Pre-aggregates unassigned rows into a single UNASSIGNED_VALUE entry so the
  // chart-grouping reducer weights it via item.count alongside real groups
  // (vs. the meaningless `total + unassignedValue` for non-summation aggregators).
  const buildSyntheticEntry = (data, unassignedValue) => {
    if (typeof unassignedValue !== 'number') return null;
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const count = (data.unassigned ?? []).filter((r) => !Number.isNaN(parseFloat(r.aggValue))).length;
    if (count === 0) return null;
    return { val: UNASSIGNED_VALUE, count, [aggregator]: unassignedValue };
  };

  const decodeUnassigned = (data) => {
    if (isAlwaysSet(chart.value.groupBy)) return null;
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    if (hasStacking.value) {
      const entries = transformRows(data.unassigned ?? []);
      (data.unassignedNoStack ?? []).forEach((row) => {
        const parsed = parseFloat(row.aggValue);
        if (!Number.isNaN(parsed)) {
          entries.push({ count: 1, [aggregator]: parsed, stackGroup: UNASSIGNED_VALUE });
        }
      });
      return entries;
    }
    return aggregateValues(data.unassigned ?? [], aggregator);
  };

  const buildResBlock = (scopeFilter) => buildBlock('res', scopeFilter);
  const buildNoStackBlock = (noStackFilter) => buildBlock('resNoStack', noStackFilter);

  const buildUnassignedBlocks = (unassignedFilter, stackOptions) => {
    const blocks = [buildBlock('unassigned', unassignedFilter)];
    if (hasStacking.value && !isAlwaysSet(stackOptions)) {
      const notHasStackFilter = buildNotHasFilter(stackOptions);
      const noStackUnassignedFilter = combine(AND, [unassignedFilter, notHasStackFilter]);
      blocks.push(buildBlock('unassignedNoStack', noStackUnassignedFilter));
    }
    return blocks;
  };

  const buildMetricBlock = (scopeFilter) => ({
    alias: 'res',
    func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
    filter: combine(AND, [scopeFilter, aggHasValueFilter()]),
    model: chart.value.source,
    children: [
      { alias: 'aggValue', attr: 'cachedProperty', langs: getLangs(chart.value.aggregationConfig.aggregation) },
    ],
  });

  const decodeMetric = (data, aggregator) => aggregateValues(data.res ?? [], aggregator);

  const decodeChartData = (data) => {
    const { realRows: realResRows, syntheticRows: syntheticResRows } = partitionRows(data.res ?? []);
    const { realRows: realNoStackRows, syntheticRows: syntheticNoStackRows } = partitionRows(data.resNoStack ?? []);
    const hasSynthetic = syntheticResRows.length > 0 || syntheticNoStackRows.length > 0;
    // Synthetic noStack rows must route to `unassignedNoStack` (which stamps
    // `stackGroup: UNASSIGNED_VALUE`) — not `unassigned`, where transformRows
    // would normalize their missing stack edge to `stackGroup: undefined` and
    // chart-grouping would file them under a phantom stack.
    const effectiveData = hasSynthetic
      ? {
        ...data,
        unassigned: [...(data.unassigned ?? []), ...syntheticResRows],
        unassignedNoStack: [...(data.unassignedNoStack ?? []), ...syntheticNoStackRows],
      }
      : data;
    const hasUnassignedQuery = chart.value.groupBy.groupBy !== chartGroupByOption.none
      && !isGoalCycleGroupingWithFilter.value;
    const unassignedValue = hasUnassignedQuery ? decodeUnassigned(effectiveData) : null;
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const unassignedEntry = !hasStacking.value && isNumericAggregator(aggregator)
      ? buildSyntheticEntry(effectiveData, unassignedValue)
      : null;
    return {
      groupByData: [...transformRows(realResRows), ...(unassignedEntry ? [unassignedEntry] : [])],
      unassignedValue,
      noStackData: transformRows(realNoStackRows),
    };
  };

  return { buildResBlock, buildMetricBlock, buildNoStackBlock, buildUnassignedBlocks, decodeMetric, decodeChartData };
}
